import React from 'react';
import {
    CommandBar,
    DetailsList,
    DetailsListLayoutMode,
    IColumn,
    IconButton,
    Pivot,
    PivotItem,
    SearchBox,
    SelectionMode,
    Spinner,
    Stack,
    Text,
} from '@fluentui/react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { buildAreaDeepLink, buildDeepLink, findArea, ISettingArea, ISettingDescriptor } from '../../settings/registry';
import { INSTANCE_TYPES } from '../../settings/instances';
import { useUrlTabState } from '../../hooks/useUrlState';
import { useVisibleSettings } from '../../hooks/useVisibleSettings';
import { RootState } from '../../store/rootReducer';
import { useGetOrganizationEntitlementsQuery } from '../../store/endpoints/OrganizationsEndpoints';

interface IReferenceRow {
    key: string;
    id: string;
    label: string;
    areaLabel: string;
    instanceLabel: string;
    permission: string;
    setting: ISettingDescriptor;
}

/**
 * Every deep-linkable setting, with the link to copy.
 *
 * This is the page a documentation author works from: search for the setting they are writing
 * about, copy its permanent link, paste it into the article. It doubles as the fastest way to see
 * what is not registered yet.
 */
const SettingsReferencePage: React.FC = () => {
    const history = useHistory();
    const [search, setSearch] = React.useState('');
    const [areaSearch, setAreaSearch] = React.useState('');
    const [selectedTab, setSelectedTab] = useUrlTabState('settings');

    /*
     * This whole page is an Adaptive Catalog staff tool, not a customer-facing feature, so it is
     * gated on an org entitlement (settingsReferenceEnabled) rather than a permission claim - see
     * SettingsLayout's nav entry for the matching gate on the link that gets here.
     *
     * An empty/loading entitlements response means NOT YET KNOWN, not denied (the same lesson
     * AssetDetailsPanel's `usageKnown` encodes for usage lookups) - redirecting on a still-loading
     * `false` would kick a legitimate staff viewer back to /settings before their own entitlements
     * query had a chance to resolve. Only redirect once the query has actually settled.
     */
    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const entitlements = useGetOrganizationEntitlementsQuery(selectedOrganization ?? '', {
        skip: !selectedOrganization,
    });
    const entitlementsKnown = !entitlements.isUninitialized && !entitlements.isFetching && entitlements.data !== undefined;
    const hasAccess = entitlements.data?.settingsReferenceEnabled === true;

    React.useEffect(() => {
        if (entitlementsKnown && !hasAccess) {
            history.replace('/settings');
        }
    }, [entitlementsKnown, hasAccess, history]);

    /*
     * This page lists EVERY registered setting and screen to whoever opens it, which is exactly what
     * would leak an entitlement-gated feature (procurement, promos, recommendations, ...) to a viewer
     * who does not have it: seeing "Honor Vendor Promos" on the reference page, with its identifier
     * and description, tells them the feature exists even though the product never shows it to them.
     *
     * `useVisibleSettings` applies the same claim filter the screens themselves are gated behind
     * (RequireAuthorization mirrors this server-side) - see its doc comment for why it lives outside
     * the registry and reads the claims slice directly rather than looping `useCan()`.
     */
    const { visibleAreas, visibleSettings } = useVisibleSettings();

    const rows: IReferenceRow[] = React.useMemo(() => {
        const term = search.trim().toLowerCase();
        return visibleSettings
            .filter(
                (s) =>
                    !term ||
                    s.id.toLowerCase().includes(term) ||
                    s.label.toLowerCase().includes(term) ||
                    (s.description ?? '').toLowerCase().includes(term)
            )
            .map((setting) => {
                const area = findArea(setting.area);
                return {
                    key: setting.id,
                    id: setting.id,
                    label: setting.label,
                    areaLabel: area?.label ?? setting.area,
                    instanceLabel: area?.instanceType ? INSTANCE_TYPES[area.instanceType].label : '',
                    permission: setting.permission ?? area?.permission ?? '',
                    setting,
                };
            })
            .sort((a, b) => a.id.localeCompare(b.id));
    }, [search, visibleSettings]);

    const areaRows: ISettingArea[] = React.useMemo(() => {
        const term = areaSearch.trim().toLowerCase();
        return visibleAreas.filter(
            (a) =>
                !term ||
                a.key.toLowerCase().includes(term) ||
                a.label.toLowerCase().includes(term) ||
                (a.description ?? '').toLowerCase().includes(term)
        ).slice();
    }, [areaSearch, visibleAreas]);

    const copyToClipboard = async (link: string) => {
        try {
            await navigator.clipboard.writeText(link);
            toast.success('Link copied');
        } catch {
            toast.error('Could not copy the link to the clipboard');
        }
    };

    const copyLink = (id: string) => copyToClipboard(buildDeepLink(id));

    const areaColumns: IColumn[] = [
        {
            key: 'areaActions',
            name: '',
            minWidth: 72,
            maxWidth: 72,
            onRender: (area: ISettingArea) => (
                <Stack horizontal>
                    <IconButton
                        iconProps={{ iconName: 'Copy' }}
                        title='Copy the documentation link'
                        onClick={() => copyToClipboard(buildAreaDeepLink(area.key))}
                    />
                    <IconButton
                        iconProps={{ iconName: 'OpenInNewWindow' }}
                        title='Open this screen'
                        onClick={() => history.push(`/go/area/${encodeURIComponent(area.key)}`)}
                    />
                </Stack>
            ),
        },
        {
            key: 'areaLabel',
            name: 'Screen',
            fieldName: 'label',
            minWidth: 200,
            maxWidth: 320,
            isResizable: true,
            onRender: (area: ISettingArea) => (
                <Stack>
                    <Text>{area.label}</Text>
                    {area.description && (
                        <Text variant='small' styles={{ root: { opacity: 0.75 } }}>
                            {area.description}
                        </Text>
                    )}
                </Stack>
            ),
        },
        {
            key: 'areaKey',
            name: 'Key',
            fieldName: 'key',
            minWidth: 220,
            maxWidth: 300,
            isResizable: true,
            onRender: (area: ISettingArea) => (
                <Text styles={{ root: { fontFamily: 'monospace' } }}>{area.key}</Text>
            ),
        },
        {
            key: 'areaInstance',
            name: 'Per',
            minWidth: 90,
            maxWidth: 120,
            isResizable: true,
            onRender: (area: ISettingArea) =>
                area.instanceType ? <Text>{INSTANCE_TYPES[area.instanceType].label}</Text> : null,
        },
        {
            key: 'areaSettings',
            name: 'Fields',
            minWidth: 70,
            maxWidth: 90,
            onRender: (area: ISettingArea) => (
                <Text>{visibleSettings.filter((s) => s.area === area.key).length || ''}</Text>
            ),
        },
    ];

    const columns: IColumn[] = [
        {
            key: 'actions',
            name: '',
            minWidth: 72,
            maxWidth: 72,
            onRender: (row: IReferenceRow) => (
                <Stack horizontal>
                    <IconButton
                        iconProps={{ iconName: 'Copy' }}
                        title='Copy the documentation link'
                        onClick={() => copyLink(row.id)}
                    />
                    <IconButton
                        iconProps={{ iconName: 'OpenInNewWindow' }}
                        title='Open this setting'
                        onClick={() => history.push(`/go/setting/${encodeURIComponent(row.id)}`)}
                    />
                </Stack>
            ),
        },
        {
            key: 'label',
            name: 'Setting',
            fieldName: 'label',
            minWidth: 160,
            maxWidth: 260,
            isResizable: true,
            onRender: (row: IReferenceRow) => (
                <Stack>
                    <Text>{row.label}</Text>
                    {row.setting.description && (
                        <Text variant='small' styles={{ root: { opacity: 0.75 } }}>
                            {row.setting.description}
                        </Text>
                    )}
                    {row.setting.condition && (
                        <Text variant='small' styles={{ root: { opacity: 0.75, fontStyle: 'italic' } }}>
                            {row.setting.condition}
                        </Text>
                    )}
                </Stack>
            ),
        },
        {
            key: 'id',
            name: 'Identifier',
            fieldName: 'id',
            minWidth: 240,
            maxWidth: 340,
            isResizable: true,
            onRender: (row: IReferenceRow) => (
                <Text styles={{ root: { fontFamily: 'monospace' } }}>{row.id}</Text>
            ),
        },
        { key: 'area', name: 'Where', fieldName: 'areaLabel', minWidth: 200, maxWidth: 320, isResizable: true },
        {
            key: 'instance',
            name: 'Per',
            fieldName: 'instanceLabel',
            minWidth: 90,
            maxWidth: 120,
            isResizable: true,
        },
        {
            key: 'permission',
            name: 'Permission',
            fieldName: 'permission',
            minWidth: 120,
            maxWidth: 160,
            isResizable: true,
        },
    ];

    // Not yet known: render nothing definite rather than flashing a denial while the entitlements
    // query is still in flight. Known and absent: the effect above is already redirecting away, so
    // render nothing here either rather than letting the page contents flash first.
    if (!entitlementsKnown) {
        return (
            <Stack horizontalAlign='center' styles={{ root: { paddingTop: '4em' } }}>
                <Spinner label='Loading…' />
            </Stack>
        );
    }
    if (!hasAccess) {
        return null;
    }

    return (
        <div>
            <h1>Settings Reference</h1>
            <Text>
                Every setting with a permanent link. Copy a link into documentation and it opens the right screen,
                picks the right tab, and highlights the field - even if the screen moves later.
            </Text>
            <CommandBar
                items={[
                    {
                        key: 'manifest',
                        text: 'Download manifest',
                        iconProps: { iconName: 'Download' },
                        onClick: () => {
                            /*
                             * Built from visibleSettings, not SETTINGS or the build-time
                             * public/settings-manifest.json - that static file is generated once at
                             * build time with no notion of who is asking, so serving it here would
                             * hand an unentitled viewer the very list this page otherwise filters for
                             * them. Generating it client-side from the already-filtered rows keeps
                             * the download honoring the same permission cut as the table above it.
                             */
                            const manifest = visibleSettings.map((setting) => {
                                const area = findArea(setting.area);
                                return {
                                    id: setting.id,
                                    label: setting.label,
                                    description: setting.description,
                                    condition: setting.condition,
                                    dependsOn: setting.dependsOn,
                                    area: setting.area,
                                    areaLabel: area?.label,
                                    instanceType: area?.instanceType,
                                    permission: setting.permission ?? area?.permission,
                                    link: `/go/setting/${encodeURIComponent(setting.id)}`,
                                };
                            });
                            const blob = new Blob([JSON.stringify({ version: 1, settings: manifest }, null, 2)], {
                                type: 'application/json',
                            });
                            const url = URL.createObjectURL(blob);
                            const anchor = document.createElement('a');
                            anchor.href = url;
                            anchor.download = 'settings-manifest.json';
                            anchor.click();
                            URL.revokeObjectURL(url);
                        },
                    },
                ]}
            />
            <Pivot selectedKey={selectedTab} onLinkClick={(item) => setSelectedTab(item?.props.itemKey)}>
                <PivotItem headerText='Settings' itemKey='settings'>
                    <Stack tokens={{ childrenGap: '1em' }} styles={{ root: { paddingTop: '1em' } }}>
                        <SearchBox
                            placeholder='Search settings by name, identifier or description…'
                            value={search}
                            onChange={(_, v) => setSearch(v ?? '')}
                        />
                        <Text variant='small'>
                            {rows.length} of {visibleSettings.length} settings across {visibleAreas.length} screens.
                        </Text>
                        <DetailsList
                            items={rows}
                            columns={columns}
                            selectionMode={SelectionMode.none}
                            layoutMode={DetailsListLayoutMode.justified}
                        />
                        {rows.length === 0 && <Text>No settings match "{search}".</Text>}
                        <Text variant='small'>
                            A setting missing here is not linkable yet: it needs an entry in{' '}
                            <Text variant='small' styles={{ root: { fontFamily: 'monospace' } }}>
                                src/settings/registry
                            </Text>{' '}
                            and a{' '}
                            <Text variant='small' styles={{ root: { fontFamily: 'monospace' } }}>&lt;Setting&gt;</Text>{' '}
                            around its control.
                        </Text>
                    </Stack>
                </PivotItem>
                <PivotItem headerText='Screens' itemKey='screens'>
                    <Stack tokens={{ childrenGap: '1em' }} styles={{ root: { paddingTop: '1em' } }}>
                        <Text>
                            Whole screens, for anything better documented as a place than as a field. The page
                            editors and the localization overrides configure content that varies per storefront and
                            per section, so there is no fixed field to point at - but the screen itself is worth
                            linking.
                        </Text>
                        <SearchBox
                            placeholder='Search screens by name or key…'
                            value={areaSearch}
                            onChange={(_, v) => setAreaSearch(v ?? '')}
                        />
                        <Text variant='small'>
                            {areaRows.length} of {visibleAreas.length} screens.
                        </Text>
                        <DetailsList
                            items={areaRows}
                            columns={areaColumns}
                            selectionMode={SelectionMode.none}
                            layoutMode={DetailsListLayoutMode.justified}
                        />
                        {areaRows.length === 0 && <Text>No screens match "{areaSearch}".</Text>}
                    </Stack>
                </PivotItem>
            </Pivot>
        </div>
    );
};

export default SettingsReferencePage;
